import React, { Component } from 'react';
import { VideoFilled, BeakerFilled, QuestionCircleFilled } from '@fluentui/react-icons';
import './pentities.css';
import './get-started.css';
import NavBar from './NavBar';

interface GetStartedProps {}

interface GetStartedState {}

class GetStarted extends Component<GetStartedProps, GetStartedState> {
  render() {
    return (
      <div className="pentities-page getstarted-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/12721926-hd_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">Get Started</span>
              <h1 className="pentities-hero-title">
                Start with Meta-365 the way that suits you best
              </h1>
              <p className="pentities-hero-description">
                Take a guided tour, install the free Lite plan from Microsoft AppSource, or talk to our team
                about the right plan. Whichever way you start, our products run inside the Microsoft 365 you already
                own, on your own tenant.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">Get Started</span>
              <h2 className="pentities-gendesc-title">Three simple ways to begin</h2>
              <p>
                There is no long onboarding, no infrastructure to set up and no data to migrate. Meta-365 installs
                straight into your Microsoft 365 tenant and works with what you already have.
              </p>
              <p>
                Choose how you would like to start: see it in action with our team, put it to work on your own data
                with the free Lite plan, or get help picking the right plan for your organisation.
              </p>
            </div>
          </div>
        </section>

        {/* Ways to start (white, workflow-style) */}
        <section id="ways" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Ways to start</span>
            <h2 className="pentities-workflow-title">Pick the path that fits where you are</h2>
            <p className="pentities-workflow-lead">
              Whether you want a guided walkthrough, a hands-on start on the free plan or a conversation about
              pricing, we will meet you where you are and get you productive fast.
            </p>

            <div className="gs-steps-grid">
              <div className="gs-step-card">
                <div className="gs-step-icon gs-step-demo">
                  <VideoFilled />
                </div>
                <h3>Take a Tour</h3>
                <ul className="gs-step-list">
                  <li>Let us show how Meta-365 solutions simplify your everyday work in the Microsoft 365 ecosystem.</li>
                  <li>Explore our products with our experts and discover new ways of productivity and collaboration.</li>
                  <li>Discover how to combine our products for a better experience.</li>
                </ul>
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gs-step-btn"
                >
                  Request a Demo
                </a>
              </div>

              <div className="gs-step-card">
                <div className="gs-step-icon gs-step-trial">
                  <BeakerFilled />
                </div>
                <h3>Test For Free</h3>
                <ul className="gs-step-list">
                  <li>Install the free Lite plan from the Microsoft AppSource marketplace no credit card, no time limit.</li>
                  <li>Contact our technical team for customization and technical advice.</li>
                  <li>Feel the experience of a product designed to increase your productivity.</li>
                  <li>Collect your SharePoint functionality in one place.</li>
                </ul>
                <a href="/contactus" className="gs-step-btn">Get in Touch</a>
              </div>

              <div className="gs-step-card">
                <div className="gs-step-icon gs-step-help">
                  <QuestionCircleFilled />
                </div>
                <h3>Get Help</h3>
                <ul className="gs-step-list">
                  <li>We're here to assist you. If you have specific requirements or need help choosing the right plan, reach out.</li>
                  <li>Explore our range of flexible plans and add-ons, and purchase licenses online for instant access.</li>
                  <li>Enjoy savings with our volume discounts.</li>
                </ul>
                <a href="/plans-pricing" className="gs-step-btn">Plans &amp; Pricing</a>
              </div>
            </div>
          </div>
        </section>

        {/* Explore the platform — products grid */}
        <section id="products" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">Explore the platform</span>
            <h2 className="pentities-modules-title">Everything runs inside the Microsoft 365 you already own</h2>
            <p className="pentities-modules-lead">
              One connected platform of products and modules, each native to Microsoft 365 and running on your own
              tenant.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/pentities" className="pentities-module-card">
                <h3>pEntities Platform</h3>
                <p>Contracts, cases, HR records and projects become living workspaces inside Microsoft Teams, with a domain AI agent on the data you already own.</p>
                <span className="pentities-module-link">Explore pEntities →</span>
              </a>
              <a href="/solutions/docsearch" className="pentities-module-card">
                <h3>DocSearch</h3>
                <p>Unified, permission-aware search and content management across OneDrive, SharePoint and Teams, from a single place.</p>
                <span className="pentities-module-link">Explore DocSearch →</span>
              </a>
              <a href="/solutions/pshareemail" className="pentities-module-card">
                <h3>PShareEmail</h3>
                <p>Save Outlook emails to SharePoint, OneDrive or Teams and share them to the right channels in a couple of clicks.</p>
                <span className="pentities-module-link">Explore PShareEmail →</span>
              </a>
              <a href="/solutions/searchassistant" className="pentities-module-card">
                <h3>Search Assistant</h3>
                <p>Find and act on the right content faster, with an assistant that works in the flow of your Microsoft 365 tools.</p>
                <span className="pentities-module-link">Explore Search Assistant →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default GetStarted;
